import type { Model as DateTimeFieldsetModel } from '@/components/fieldsets/DateTimeFieldset.vue';
import type { Props as DatePickerFieldProps } from '@/components/pickerFields/DatePickerField.vue';
import type { TimePickerFieldProps } from '@/components/pickerFields/TimePickerField.vue';

import { TZDate } from '@date-fns/tz';
import {
  addDays,
  addMonths,
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  formatDistanceToNow,
  parseISO,
  set,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subMonths,
} from 'date-fns';
import { format, fromZonedTime, toZonedTime } from 'date-fns-tz';

import * as datePickerLabels from '@/constants/datePickerLabels';

type DatePickerLabelsKeys = keyof typeof datePickerLabels;
type DatePickerLabelsValues = (typeof datePickerLabels)[DatePickerLabelsKeys];

export const defaultApiTimeZone = 'Australia/Sydney';

export function getApiZonedFormattedDate(
  userZonedDate: Date,
  userTimeZone: string,
): string {
  const utcDate = fromZonedTime(userZonedDate, userTimeZone);
  const apiZonedDate = toZonedTime(utcDate, defaultApiTimeZone);
  return format(apiZonedDate, 'yyyy-MM-dd');
}

export function getUserZonedTodayDate(userTimeZone: string): Date {
  // choose the right day depends on timezone
  return set(toZonedTime(new Date(), userTimeZone), {
    hours: 0,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  });
}

export function getApiZonedEndOfTodayTZDate() {
  return endOfDay(TZDate.tz(defaultApiTimeZone));
}

export function getDatePickerPresetValue(
  presetName: DatePickerLabelsValues,
  userTimeZone: string,
): [Date, Date] {
  const today = getUserZonedTodayDate(userTimeZone);

  if (presetName === datePickerLabels.TODAY) {
    return [today, today];
  } else if (presetName === datePickerLabels.YESTERDAY) {
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    return [yesterday, yesterday];
  } else if (presetName === datePickerLabels.TOMORROW) {
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return [tomorrow, tomorrow];
  } else if (presetName === datePickerLabels.LAST_7_DAYS) {
    return [addDays(today, -7), today];
  } else if (presetName === datePickerLabels.LAST_14_DAYS) {
    return [addDays(today, -14), today];
  } else if (presetName === datePickerLabels.NEXT_5_DAYS) {
    return [today, addDays(today, 5)];
  } else if (presetName === datePickerLabels.THIS_WEEK) {
    return [
      startOfWeek(today, { weekStartsOn: 1 }),
      endOfWeek(today, { weekStartsOn: 1 }),
    ];
  } else if (presetName === datePickerLabels.THIS_MONTH) {
    return [startOfMonth(today), endOfMonth(today)];
  } else if (presetName === datePickerLabels.LAST_MONTH) {
    const lastMonth = subMonths(today, 1);
    return [startOfMonth(lastMonth), endOfMonth(lastMonth)];
  } else if (presetName === datePickerLabels.THIS_YEAR) {
    return [startOfYear(today), endOfYear(today)];
  } else if (presetName === datePickerLabels.LAST_6_MTHS_NEXT_6_MTHS) {
    return [addMonths(today, -6), addMonths(today, 6)];
  } else if (presetName === datePickerLabels.LAST_2_YEARS_NEXT_2_YEARS) {
    return [addMonths(today, -24), addMonths(today, 24)];
    // TODO
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  } else if (presetName === datePickerLabels.LAST_7_DAYS_NEXT_5_DAYS) {
    return [addDays(today, -7), addDays(today, 5)];
  } else {
    throw new Error('Wrong presetName');
  }
}

export function formatDateTimeToDatePickerFieldValue(
  dateTime: string,
): DatePickerFieldProps['modelValue'] {
  return dateTime.slice(0, 10);
}

export function formatDateTimeToTimePickerFieldValue(
  dateTime: string,
): TimePickerFieldProps['modelValue'] {
  const value: TimePickerFieldProps['modelValue'] = {
    hours: NaN,
    minutes: NaN,
  };
  value.hours = parseInt(dateTime.slice(11, 13));
  value.minutes = parseInt(dateTime.slice(14, 16));
  value.seconds = parseInt(dateTime.slice(17, 19));
  return value;
}

// parseISO returns Invalid Date for bad input; formatDistanceToNow is what
// throws on it — the catch guards that path.
export function formatRelativeFromNow(iso: string | undefined | null): string {
  if (!iso) return '';
  try {
    return formatDistanceToNow(parseISO(iso), { addSuffix: true });
  } catch {
    return '';
  }
}

export function formatDateTimeToDateTimeFieldsetValue(
  dateTime?: string | null,
): DateTimeFieldsetModel {
  // TODO respect timezones
  if (!dateTime) {
    return {
      date: undefined,
      time: undefined,
    };
  }
  return {
    date: formatDateTimeToDatePickerFieldValue(dateTime),
    time: formatDateTimeToTimePickerFieldValue(dateTime),
  };
}

export function getTicketDetailsFormatDateTime(datetime: string): string {
  return format(
    new TZDate(datetime, defaultApiTimeZone),
    'yyyy-MM-dd, hh:mm aa', // 2026-05-22, 04:01 AM
  );
}
